import { useEffect, useMemo, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import Alert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";
import { IoIosArrowBack } from "react-icons/io";
import { previousBooking } from "actions/therapist";
import { fetchMarketplaceTherapistById } from "actions/marketplace";
import { useUserTokenSelector } from "store/reducers";
import { startLoading, stopLoading } from "store/actions";
import "../Calendar Booking/index.css";
import "./MarketplaceBookingScreen.css";

const TESTIMONIAL_DISPLAY_NAMES = [
  "Aarav, Delhi",
  "Deep, Mumbai",
  "Nikita, Chandigarh",
  "Rohan, Bengaluru",
  "Priya, Jaipur",
  "Ananya, Pune",
  "Karan, Ahmedabad",
  "Meera, Hyderabad",
  "Arjun, Chennai",
  "Ishita, Kolkata",
  "Rahul, Lucknow",
  "Simran, Ludhiana",
  "Dev, Surat",
  "Tanya, Gurgaon",
  "Yash, Indore",
  "Emily, New York",
  "Michael, Chicago",
  "Sophia, Los Angeles",
  "Ethan, Houston",
  "Olivia, Seattle",
  "Daniel, Boston",
  "Ava, UK",
  "Liam, San Francisco",
  "Chloe, Austin",
  "Noah, Denver",
  "Anonymous, Lucknow",
  "Anonymous, Pune",
  "Anonymous, Hyderabad",
  "Anonymous, Mumbai",
  "Anonymous, J&K",
];

const getSeededHash = (value = "") => {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const getShuffledTestimonialNames = (seedValue) => {
  const names = [...TESTIMONIAL_DISPLAY_NAMES];
  let seed = getSeededHash(seedValue);

  for (let i = names.length - 1; i > 0; i -= 1) {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    const swapIndex = seed % (i + 1);
    [names[i], names[swapIndex]] = [names[swapIndex], names[i]];
  }

  return names;
};

const formatINR = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const formatDate = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString("en-IN");
};

const formatBookingDate = (date) => {
  const d = new Date(date);
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  const year = d.getFullYear();
  return [year, month, day].join("-");
};

const getDateToday = () => new Date(new Date().setHours(0, 0, 0, 0));

const getFutureDate = () =>
  new Date(new Date(new Date().setDate(new Date().getDate() + 30)).setHours(23, 59, 59, 0));

const TimeSlots = ({ minutes, hours, onSelect, selectedTime }) => {
  const isActive =
    hours === selectedTime.hours && minutes === selectedTime.minutes;

  return (
    <button
      type="button"
      onClick={() => onSelect({ minutes, hours })}
      className={`marketplace-booking-slot-button ${isActive ? "active" : ""}`.trim()}
    >
      {hours}:{minutes === 0 ? "00" : minutes}
    </button>
  );
};

const MarketplaceBookingScreen = ({ therapist }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const userToken = useUserTokenSelector();
  const [currentDate, setCurrentDate] = useState("");
  const [selectedTime, setSelectedTime] = useState({});
  const [availableSlots, setAvailableSlots] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [allEvents, setAllEvents] = useState({});
  const [therapistDetails, setTherapistDetails] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [showAllProblemAreas, setShowAllProblemAreas] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [expandedPeriods, setExpandedPeriods] = useState({
    morning: false,
    afternoon: false,
    evening: false,
  });

  const therapistData = therapist || location.state?.marketplaceTherapist;
  const displayTherapist = therapistDetails || therapistData;

  useEffect(() => {
    if (!therapistData?._id) {
      history.replace("/dashboard/overview");
      return;
    }

    const loadTherapist = async () => {
      try {
        const response = await fetchMarketplaceTherapistById(therapistData._id);
        if (response.data?.therapist) {
          setTherapistDetails(response.data.therapist);
        }
      } catch (err) {
        setTherapistDetails(therapistData);
      }
    };

    const loadSlots = async () => {
      try {
        dispatch(startLoading());
        const response = await previousBooking(
          {
            therapistID: therapistData._id,
            userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          userToken
        );

        const events = response.data?.allEvents || {};
        const dates = Object.keys(events)
          .map((date) => {
            const [day, month, year] = date.split("/").map(Number);
            return new Date(year, month - 1, day);
          })
          .filter(Boolean)
          .sort((a, b) => a - b);

        setAllEvents(events);
        const openDates = dates.filter((date) => events[formatDate(date)]?.length > 0);
        setAvailableDates(openDates);
        if (openDates.length > 0) {
          const firstDate = openDates[0];
          setCurrentDate(firstDate);
          setAvailableSlots((events[formatDate(firstDate)] || []).slice().sort((a, b) => {
            if (a.hours === b.hours) {
              return a.minutes - b.minutes;
            }
            return a.hours - b.hours;
          }));
        }
        dispatch(stopLoading());
      } catch (err) {
        dispatch(stopLoading());
        setError(err?.response?.data?.error || "Unable to load therapist slots.");
        setOpen(true);
      }
    };

    loadSlots();
    loadTherapist();
  }, [dispatch, history, therapistData, userToken]);

  const groupedSlots = useMemo(() => {
    const grouped = {
      morning: [],
      afternoon: [],
      evening: [],
    };

    availableSlots.forEach(({ hours, minutes }) => {
      if (hours >= 6 && hours < 12) {
        grouped.morning.push({ hours, minutes });
      } else if (hours >= 12 && hours < 17) {
        grouped.afternoon.push({ hours, minutes });
      } else if (hours >= 17 && hours <= 23) {
        grouped.evening.push({ hours, minutes });
      }
    });

    return grouped;
  }, [availableSlots]);

  const toggleExpand = (period) => {
    setExpandedPeriods((prev) => ({
      morning: period === "morning" ? !prev.morning : false,
      afternoon: period === "afternoon" ? !prev.afternoon : false,
      evening: period === "evening" ? !prev.evening : false,
    }));
  };

  const handleSlotSelect = (date) => {
    const dateKey = formatDate(date);
    setCurrentDate(date);
    setSelectedTime({});
    setAvailableSlots((allEvents[dateKey] || []).slice().sort((a, b) => {
      if (a.hours === b.hours) {
        return a.minutes - b.minutes;
      }
      return a.hours - b.hours;
    }));
  };

  const handleContinue = () => {
    if (!currentDate || Object.keys(selectedTime).length === 0) {
      return;
    }

    history.push("/sessionBooking/payment-plans", {
      routeProps: { categoryId: 1, currency: "INR", therapistId: therapistData?._id },
      marketplaceTherapist: therapistData,
      selectedDate: formatBookingDate(currentDate),
      selectedTime,
      selectedTimeLabel: `${selectedTime.hours}:${selectedTime.minutes === 0 ? "00" : selectedTime.minutes}`,
    });
  };

  const handleClose = (_, reason) => {
    if (reason === "clickaway") {
      setOpen(false);
      return;
    }
    setOpen(false);
  };

  const therapistTags = (displayTherapist?.specialities || [])
    .map((speciality) => speciality.problemCategory || speciality.problemID)
    .filter(Boolean);
  const visibleTherapistTags = showAllProblemAreas
    ? therapistTags
    : therapistTags.slice(0, 3);
  const hasMoreThanThreeProblemAreas = therapistTags.length > 3;
  const pricing = displayTherapist?.pricing || therapistData?.pricing || {};
  const ratingValue = displayTherapist?.ratings?.average || 0;
  const ratingCount = displayTherapist?.ratings?.count || 0;
  const sessionsDoneCount = displayTherapist?.sessionsDoneCount || 0;
  const reviews = displayTherapist?.reviews || [];
  const testimonialNames = useMemo(
    () =>
      getShuffledTestimonialNames(
        displayTherapist?._id || therapistData?._id || "marketplace-booking"
      ),
    [displayTherapist?._id, therapistData?._id]
  );
  const description = displayTherapist?.description || "";
  const descriptionWords = description.trim().split(/\s+/).filter(Boolean);
  const hasLongDescription = descriptionWords.length > 100;
  const descriptionToShow =
    !hasLongDescription || showFullDescription
      ? description
      : descriptionWords.slice(0, 100).join(" ");
  const languages = displayTherapist?.languages || [];
  const imageUrl =
    displayTherapist?.therapistImageUrl ||
    displayTherapist?.therapistImage ||
    "/images/therapist-demo.png";
  const hasStats = ratingCount > 0 || sessionsDoneCount > 0;
  const statCards = [
    ratingCount > 0
      ? { value: ratingValue.toFixed(1), label: "Rating", icon: "★" }
      : null,
    ratingCount > 0 ? { value: ratingCount, label: "Reviews" } : null,
    sessionsDoneCount > 0 ? { value: `${sessionsDoneCount}+`, label: "Sessions" } : null,
  ].filter(Boolean);
  const formatReviewText = (review) => {
    if (review?.feedbackMessage && review.feedbackMessage.trim()) {
      return review.feedbackMessage.trim();
    }

    if (Array.isArray(review?.comments) && review.comments.length > 0) {
      return review.comments.filter(Boolean).join(", ");
    }

    return "";
  };

  return (
    <div className="marketplace-booking-page">
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error" style={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
      <div className="marketplace-booking-shell">
        <div className="marketplace-booking-topbar">
          <button
            type="button"
            className="marketplace-booking-back"
            onClick={() => history.goBack()}
          >
            <IoIosArrowBack size={18} />
          </button>
          <h2 className="marketplace-booking-title">Choose A Slot</h2>
          <div className="marketplace-booking-topbar-spacer" />
        </div>

        <div className="marketplace-booking-progress">
          <div className="marketplace-booking-progress-line" />
          <div
            className="marketplace-booking-progress-fill"
            style={{ width: "34%" }}
          />
          <div className="marketplace-booking-progress-steps">
            <div className="marketplace-booking-progress-step completed">✓</div>
            <div className="marketplace-booking-progress-step" />
          </div>
        </div>

        <div className="marketplace-booking-grid">
          <section className="marketplace-booking-therapist">
            <div className="marketplace-booking-profile-row">
              <div className="marketplace-booking-profile-media">
                <img
                  src={imageUrl}
                  alt={displayTherapist?.therapistName || "Therapist"}
                />
                {languages.length > 0 && (
                  <p className="marketplace-booking-language marketplace-booking-language-under-image">
                    Fluent in {languages.join(", ")}
                  </p>
                )}
              </div>

              <div className="marketplace-booking-profile-copy">
                <div className="marketplace-booking-therapist-copy">
                  <h1>{displayTherapist?.therapistName}</h1>
                  <p className="marketplace-booking-therapist-meta">
                    <span>{displayTherapist?.designation || "Therapist"}</span>
                    <span className="marketplace-booking-therapist-meta-separator">|</span>
                    <span>{displayTherapist?.experienceInYears || 0} Yrs</span>
                  </p>
                </div>
                {visibleTherapistTags.length > 0 && (
                  <div className="marketplace-booking-tags">
                    {visibleTherapistTags.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                )}

                {hasMoreThanThreeProblemAreas ? (
                  <button
                    type="button"
                    className="marketplace-booking-text-toggle marketplace-booking-tags-toggle"
                    onClick={() => setShowAllProblemAreas((current) => !current)}
                  >
                    {showAllProblemAreas ? "Show less" : "Show more"}
                  </button>
                ) : null}

                {description ? (
                  <div className="marketplace-booking-about">
                    <p className="marketplace-booking-section-title">About</p>
                    <p className="marketplace-booking-bio">
                      {descriptionToShow}
                      {hasLongDescription && !showFullDescription ? "..." : ""}
                    </p>
                    {hasLongDescription ? (
                      <button
                        type="button"
                        className="marketplace-booking-text-toggle"
                        onClick={() => setShowFullDescription((current) => !current)}
                      >
                        {showFullDescription ? "Show less" : "Show more"}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            {hasStats ? (
              <div className="marketplace-booking-stats">
                {statCards.map((card) => (
                  <div key={card.label} className="marketplace-booking-stat-card">
                    <div className="marketplace-booking-stat-value">
                      {card.icon ? <span className="marketplace-booking-stat-icon">{card.icon}</span> : null}
                      <strong>{card.value}</strong>
                    </div>
                    <p>{card.label}</p>
                  </div>
                ))}
              </div>
            ) : null}

            {reviews.length > 0 ? (
              <div className="marketplace-booking-testimonials">
                <p className="marketplace-booking-section-title">Testimonials</p>
                <div className="marketplace-booking-testimonial-grid">
                  {reviews.slice(0, 4).map((review, index) => (
                    <div
                      className="marketplace-booking-testimonial-card"
                      key={`${review.feedbackTimestamp || index}`}
                    >
                      <div className="marketplace-booking-testimonial-head">
                        <span>{review.reviewerName || testimonialNames[index]}</span>
                        {review.rating ? <strong>★ {review.rating}</strong> : null}
                      </div>
                      {formatReviewText(review) ? (
                        <p>{formatReviewText(review)}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </section>

          <section className="marketplace-booking-slot-panel">
            <div className="calendar-rccbooking marketplace-booking-calendar-card">
              <p className="calendar-heading-subtitle">
                Calendar displayed in India Standard Time
                <br />
                (GMT+0530 Timezone)
              </p>
              <Calendar
                onChange={handleSlotSelect}
                value={currentDate}
                showNeighboringMonth={false}
                defaultView="month"
                tileDisabled={({ date }) => date < getDateToday() || date > getFutureDate()}
                tileClassName={({ date, view }) => {
                  if (
                    view === "month" &&
                    availableDates.some((item) => item.toDateString() === date.toDateString())
                  ) {
                    return "available-slot";
                  }
                  return "";
                }}
              />
              <div className="calendar-legend">
                <div className="legend-item">
                  <span className="selected-box"></span> Selected
                </div>
                <div className="legend-item">
                  <span className="available-box"></span> Available
                </div>
              </div>
            </div>

            <div
              className="timeslots-rccbooking marketplace-booking-timeslots"
              style={{
                opacity: currentDate ? 1 : 0,
                pointerEvents: currentDate ? "all" : "none",
              }}
            >
              <p>Choose Session time</p>
              <div className="choose-session-container marketplace-booking-choose-session">
                {["morning", "afternoon", "evening"].map((period) => {
                  const slots = groupedSlots[period] || [];
                  const isExpanded = expandedPeriods[period];

                  return slots.length > 0 ? (
                    <div
                      key={period}
                      className="time-slot-category marketplace-booking-time-slot-category"
                    >
                      <h5 className="time-period-title marketplace-booking-time-period-title">
                        {period.charAt(0).toUpperCase() + period.slice(1)}
                      </h5>
                      <div className="slots-div">
                        {slots
                          .slice(0, isExpanded ? slots.length : 5)
                          .map((slot) => (
                            <TimeSlots
                              key={`${slot.hours}:${slot.minutes}`}
                              hours={slot.hours}
                              minutes={slot.minutes}
                              selectedTime={selectedTime}
                              onSelect={setSelectedTime}
                            />
                          ))}
                        {slots.length > 5 && (
                          <button
                            type="button"
                            className="marketplace-booking-see-more-btn"
                            onClick={() => toggleExpand(period)}
                          >
                            {isExpanded ? "See Less" : "See More"}
                          </button>
                        )}
                      </div>
                    </div>
                  ) : null;
                })}
              </div>
            </div>

            <div className="marketplace-booking-footer">
              <button
                type="button"
                className="marketplace-booking-continue"
                disabled={!currentDate || Object.keys(selectedTime).length === 0}
                onClick={handleContinue}
              >
                Continue
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceBookingScreen;
